import {
  getProductListingClient,
  IListingClientSearchIntent,
} from "@tm/insurance/api";
import { persistListingContext, replaceHistory } from "@tm/insurance/utils";
import { useCallback, useState } from "react";
import { ListingRevampProps } from "../types";

type ListingData = ListingRevampProps["listingData"];

interface UseListingDataReturn {
  listingData: ListingData | undefined;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  refetch: (searchIntent: IListingClientSearchIntent) => Promise<void>;
}

export const useListingData = (
  initialData: ListingData | undefined
): UseListingDataReturn => {
  const [listingData, setListingData] = useState<ListingData | undefined>(
    initialData
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(
    async (searchIntent: IListingClientSearchIntent) => {
      setIsLoading(true);
      setError(null);

      try {
        const currentQuery =
          typeof window !== "undefined"
            ? (Object.fromEntries(
                new URLSearchParams(window.location.search).entries()
              ) as Record<string, string>)
            : {};
        const response = await getProductListingClient(
          searchIntent,
          currentQuery
        );
        const data = response?.data ?? response;

        if (data?.responseStatus === "ERROR") {
          const errorTitle =
            data?.error?.message?.title || "Failed to fetch listing data";
          setError(errorTitle);
          return;
        }

        setListingData(data);

        syncUrlParams(searchIntent);
        persistListingContext();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch listing data";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const clearError = useCallback(() => setError(null), []);

  return { listingData, isLoading, error, clearError, refetch };
};

function syncUrlParams(intent: IListingClientSearchIntent) {
  if (intent.country?.length) {
    replaceHistory("countries", intent.country.join(","));
  }
  if (intent.departureDateTime) {
    replaceHistory("startDate", String(intent.departureDateTime));
  }
  if (intent.arrivalDateTime) {
    replaceHistory("endDate", String(intent.arrivalDateTime));
  }
  if (intent.numberOfTravellers) {
    replaceHistory("travellers", String(intent.numberOfTravellers));
  }
  replaceHistory("tripOption", intent.tripOption ?? "");
  replaceHistory("planType", intent.planType ?? "");
  replaceHistory("callerSource", intent.callerSource ?? "");
}
